// AllDetails.js
import React, { useEffect, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase-config";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";

/* ---------------- AI-ish visual bits ---------------- */

// Subtle ambient sparkles
const Sparkles = () => {
  const dots = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 92 + 4}%`,
        top: `${Math.random() * 60 + 8}%`,
        size: Math.random() * 6 + 3,
        dur: Math.random() * 4 + 3,
        delay: Math.random() * 2,
      })),
    []
  );

  return (
    <div
      aria-hidden
      style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}
    >
      {dots.map((d) => (
        <motion.span
          key={d.id}
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: [-8, 8, -8], opacity: [0.12, 0.3, 0.12] }}
          transition={{ duration: d.dur, repeat: Infinity, delay: d.delay, ease: "easeInOut" }}
          style={{
            position: "absolute",
            left: d.left,
            top: d.top,
            width: d.size,
            height: d.size,
            borderRadius: 999,
            background:
              "radial-gradient(circle at 30% 30%, rgba(255,255,255,.9), rgba(255,255,255,.0) 60%)",
            filter: "blur(.6px)",
          }}
        />
      ))}
    </div>
  );
};

// Confetti burst for happy moments
const Confetti = ({ show = false }) => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 28 }).map((_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 260,
        y: (Math.random() - 0.5) * 70,
        r: Math.random() * 360,
        s: Math.random() * 8 + 4,
        d: Math.random() * 0.6 + 0.6,
      })),
    []
  );
  if (!show) return null;
  return (
    <div style={{ position: "fixed", top: "14%", left: "50%", zIndex: 9999, pointerEvents: "none" }}>
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
          animate={{ x: p.x, y: p.y + 160, rotate: p.r, opacity: [1, 1, 0] }}
          transition={{ duration: 1.3 * p.d, ease: "easeOut" }}
          style={{
            width: p.s,
            height: p.s,
            borderRadius: 2,
            background:
              ["#60A5FA", "#34D399", "#F472B6", "#FBBF24", "#A78BFA"][p.id % 5],
          }}
        />
      ))}
    </div>
  );
};

// Toast notifications
const Toast = ({ type = "success", text, onDone }) => {
  const colors =
    type === "success"
      ? ["#10B981", "#34D399"]
      : type === "info"
      ? ["#3B82F6", "#60A5FA"]
      : ["#EF4444", "#F87171"];
  const icon = type === "success" ? "✓" : type === "info" ? "ℹ" : "✖";

  return (
    <motion.div
      initial={{ y: -30, opacity: 0, scale: 0.98 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: -30, opacity: 0, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      onAnimationComplete={() => setTimeout(onDone, 1400)}
      style={{
        position: "fixed",
        top: 18,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10000,
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderRadius: 12,
          color: "white",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,.15)",
          background: `linear-gradient(90deg, ${colors[0]}, ${colors[1]})`,
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            display: "grid",
            placeItems: "center",
            background: "rgba(255,255,255,.25)",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          {icon}
        </span>
        <span>{text}</span>
      </div>
    </motion.div>
  );
};

// Shimmer skeleton row
const SkeletonRow = () => (
  <tr>
    {Array.from({ length: 12 }).map((_, i) => (
      <td key={i}>
        <div
          style={{
            height: 12,
            borderRadius: 6,
            background:
              "linear-gradient(90deg, rgba(0,0,0,.06), rgba(0,0,0,.12), rgba(0,0,0,.06))",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.2s linear infinite",
          }}
        />
      </td>
    ))}
  </tr>
);

/* ---------------- Main Component ---------------- */

const AllDetails = () => {
  const [details, setDetails] = useState([]);
  const [searchQueries, setSearchQueries] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState("50");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null); // {type, text}
  const [confetti, setConfetti] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [rowGlowId, setRowGlowId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      navigate("/");
    } else {
      fetchDetails(userId);
    }
    // shimmer keyframes (once)
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: 0% 0; }
        100% { background-position: 200% 0; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [navigate]);

  const fetchDetails = async (userId) => {
    setLoading(true);
    try {
      const detailsRef = collection(db, "credit-card-details");
      const querySnapshot = await getDocs(detailsRef);
      const fetchedDetails = querySnapshot.docs
        .map((docu) => ({ ...docu.data(), id: docu.id }))
        .filter((detail) => detail.userId === userId)
        .sort((a, b) => (b.createDate || "").localeCompare(a.createDate || ""));
      setDetails(fetchedDetails);
    } catch (error) {
      console.error("Error fetching details from Firestore:", error);
    }
    setLoading(false);
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(details);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AllDetails");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "all_details.xlsx");
    setToast({ type: "info", text: "Exported to Excel" });
    setConfetti(true);
    setTimeout(() => setConfetti(false), 1000);
  };

  const handleCopy = (detail) => {
    const valuesToCopy = [
      detail.registeredMobileNumber,
      detail.name,
      (detail.creditCardNumber || "").replace(/\s+/g, ""),
      detail.expiryDate,
      detail.cvv,
      detail.providerBank,
      detail.cardType,
    ].join("\n");
    navigator.clipboard.writeText(valuesToCopy);
    setToast({ type: "info", text: "Copied to clipboard" });
    setRowGlowId(detail.id);
    setTimeout(() => setRowGlowId(null), 900);
  };

  const askDelete = (id) => setConfirmDelete(id);

  const handleDelete = async () => {
    const id = confirmDelete;
    if (!id) return;
    try {
      setDeletingId(id);
      await deleteDoc(doc(db, "credit-card-details", id));
      setDetails((prev) => prev.filter((d) => d.id !== id));
      setToast({ type: "success", text: "Deleted successfully" });
    } catch (error) {
      console.error("Error deleting document:", error);
      setToast({ type: "error", text: "Delete failed" });
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const handleColumnSearchChange = (e, column) => {
    setSearchQueries((prev) => ({ ...prev, [column]: e.target.value }));
  };

  const filteredDetails = details.filter((detail) =>
    Object.entries(searchQueries).every(([column, query]) => {
      if (!query) return true;
      const value = detail[column];
      return typeof value === "string" && value.toLowerCase().includes(query.toLowerCase());
    })
  );

  const paginate = () => {
    if (itemsPerPage === "All") return filteredDetails;
    const end = parseInt(itemsPerPage, 10);
    return filteredDetails.slice(0, end);
    // (First page only per your original logic; extend to full pagination if needed)
  };

  return (
    <div style={{ position: "relative" }}>
      <Sparkles />
      <Confetti show={confetti} />
      <AnimatePresence>
        {toast && (
          <Toast key={toast.text} type={toast.type} text={toast.text} onDone={() => setToast(null)} />
        )}
      </AnimatePresence>

      {/* Command bar */}
      <motion.div
        className="d-flex justify-content-between align-items-center mb-3"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          gap: 12,
          flexWrap: "wrap",
          background:
            "linear-gradient(90deg, rgba(59,130,246,.08), rgba(236,72,153,.08))",
          padding: 12,
          borderRadius: 12,
          border: "1px solid rgba(99,102,241,.15)",
          boxShadow: "0 8px 28px -16px rgba(99,102,241,.25)",
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <motion.button
            whileHover={{ scale: 1.03, y: -1 }}
            whileTap={{ scale: 0.98 }}
            className="btn"
            onClick={handleExport}
            style={{
              background:
                "linear-gradient(90deg, rgba(16,185,129,.95), rgba(5,150,105,.95))",
              color: "white",
              border: 0,
              padding: "10px 16px",
              borderRadius: 12,
              boxShadow: "0 10px 22px -12px rgba(16,185,129,.55)",
              fontWeight: 700,
              letterSpacing: ".3px",
            }}
          >
            ⬇ Export Excel
          </motion.button>

          <select
            className="form-select"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(e.target.value)}
            style={{
              width: 120,
              borderRadius: 10,
              border: "1px solid rgba(99,102,241,.25)",
            }}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="All">All</option>
          </select>
        </div>

        {/* Nice global search across columns (optional: keep column search too) */}
        <div className="d-flex" style={{ gap: 8 }}>
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Quick filter: name / mobile / bank / type…"
              onChange={(e) =>
                setSearchQueries((prev) => ({ ...prev, __global: e.target.value }))
              }
              style={{
                paddingLeft: 38,
                height: 42,
                borderRadius: 12,
                border: "1px solid rgba(99,102,241,.25)",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,.04)",
              }}
            />
            <span
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                opacity: 0.65,
                fontSize: 18,
              }}
            >
              🔎
            </span>
          </div>
        </div>
      </motion.div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead
            style={{
              backgroundColor: "#e6f0ff",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <tr>
              <th className="px-3">
                Mobile
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "registeredMobileNumber")}
                />
              </th>
              <th className="px-3">
                Name
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "name")}
                />
              </th>
              <th className="px-3">
                CC Number
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "creditCardNumber")}
                />
              </th>
              <th className="px-3">
                Exp
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "expiryDate")}
                />
              </th>
              <th className="px-3">
                CVV
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "cvv")}
                />
              </th>
              <th className="px-3">
                Bank
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "providerBank")}
                />
              </th>
              <th className="px-3">
                CC Type
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "cardType")}
                />
              </th>
              <th className="px-3">
                BG Date
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "billGeneratedDate")}
                />
              </th>
              <th className="px-3">
                Due Date
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "billDueDate")}
                />
              </th>
              <th className="px-3">
                Referred
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => handleColumnSearchChange(e, "referredBy")}
                />
              </th>
              <th className="px-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {/* Loading skeleton */}
            {loading &&
              Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={`sk-${i}`} />)}

            {/* Data rows */}
            {!loading && paginate().length > 0 ? (
              <AnimatePresence initial={false}>
                {paginate().map((detail) => {
                  const cardNumber = (detail.creditCardNumber || "").replace(/\s+/g, "");
                  const glow = rowGlowId === detail.id;
                  const globalQuery = (searchQueries.__global || "").toLowerCase();
                  const inGlobal =
                    !globalQuery ||
                    [
                      detail.name,
                      detail.registeredMobileNumber,
                      detail.providerBank,
                      detail.cardType,
                      detail.remarks,
                      detail.referredBy,
                      cardNumber,
                    ]
                      .filter(Boolean)
                      .some((v) => String(v).toLowerCase().includes(globalQuery));

                  if (!inGlobal) return null;

                  return (
                    <motion.tr
                      key={detail.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        backgroundColor: glow ? "rgba(16,185,129,.08)" : "transparent",
                      }}
                      exit={{
                        opacity: 0,
                        y: -8,
                        backgroundColor: "rgba(239,68,68,.08)",
                      }}
                      transition={{ duration: 0.25 }}
                      style={{ position: "relative" }}
                    >
                      {glow && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: [0.7, 0], scale: [1, 1.05] }}
                          transition={{ duration: 0.9 }}
                          style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: 10,
                            border: "2px solid rgba(16,185,129,.45)",
                            pointerEvents: "none",
                          }}
                        />
                      )}
                      <td className="px-3">{detail.registeredMobileNumber}</td>
                      <td className="px-3">{detail.name}</td>
                      <td className="px-3">{cardNumber}</td>
                      <td className="px-3">{detail.expiryDate}</td>
                      <td className="px-3">{detail.cvv}</td>
                      <td className="px-3">{detail.providerBank}</td>
                      <td className="px-3">{detail.cardType}</td>
                      <td className="px-3">{detail.billGeneratedDate}</td>
                      <td className="px-3">{detail.billDueDate}</td>
                      <td className="px-3">{detail.referredBy}</td>
                      <td className="px-3">
                        <div className="d-flex justify-content-between">
                          <motion.button
                            onClick={() => handleCopy(detail)}
                            className="btn btn-info btn-sm"
                            title="Copy"
                            whileHover={{ scale: 1.06 }}
                            whileTap={{ scale: 0.96 }}
                          >
                            <i className="fas fa-copy"></i>
                          </motion.button>
                          <motion.button
                            onClick={() =>
                              navigate(`/dashboard/update-details/${detail.id}`)
                            }
                            className="btn btn-warning btn-sm mx-2"
                            title="Edit"
                            whileHover={{ scale: 1.06 }}
                            whileTap={{ scale: 0.96 }}
                          >
                            <i className="fas fa-edit"></i>
                          </motion.button>
                          <motion.button
                            onClick={() => askDelete(detail.id)}
                            className="btn btn-danger btn-sm"
                            title="Delete"
                            whileHover={{ scale: 1.06 }}
                            whileTap={{ scale: 0.96 }}
                            disabled={deletingId === detail.id}
                          >
                            <i className="fas fa-trash"></i>
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            ) : (
              !loading && (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    No matching results found.
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            key="confirm"
            className="modal show d-block"
            tabIndex="-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="modal-dialog">
              <motion.div
                className="modal-content"
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.97, y: 10, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                style={{ borderRadius: 14 }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">Delete record?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setConfirmDelete(null)}
                  ></button>
                </div>
                <div className="modal-body">This action cannot be undone.</div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>
                    Cancel
                  </button>
                  <motion.button
                    className="btn btn-danger"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleDelete}
                  >
                    Delete
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AllDetails;
